import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Clock, CheckCircle2, Lock, X, Send } from "lucide-react";
import StudioBackdrop from "@/components/podcast/StudioBackdrop";

const INITIAL = {
  slot_date: "",
  full_name: "",
  email: "",
  phone: "",
  social_handle: "",
  topic: "",
  bio: "",
};

function formatDate(iso) {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function Book() {
  const [slots, setSlots] = useState([]);
  const [season, setSeason] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(true);
  const [openSlot, setOpenSlot] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/schedule/slots");
      setSlots(r.data.slots);
      setSeason({ start: r.data.season_start, end: r.data.season_end });
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const startBooking = (date) => {
    setForm({ ...INITIAL, slot_date: date });
    setOpenSlot(date);
    setSubmitted(false);
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/schedule/book", form);
      toast.success("Booking received. We'll be in touch.");
      setSubmitted(true);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Booking failed";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-page" className="pt-32 pb-20 min-h-screen bg-[#0A0A0A] relative film-grain">
      <StudioBackdrop opacity={0.14} size={260} />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-12 max-w-3xl">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E10600] mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Season 2 · Guest Schedule
          </p>
          <h1 className="font-display text-6xl md:text-7xl uppercase tracking-tight leading-[0.9]">
            Book Your <span className="text-[#1E6FE0]">Mic</span> <span className="text-[#E10600]">Time</span>
          </h1>
          <p className="mt-6 text-zinc-300 text-lg">
            Twelve weeks. Twelve conversations. Pick the Monday that works for you and
            claim your slot — first come, first booked.
          </p>
          <p className="mt-3 text-sm text-zinc-500 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#1E6FE0]" />
            Every slot: Monday · 7:00 PM – 9:00 PM EST · NYC or Boston studio (remote OK)
          </p>
        </header>

        {loading ? (
          <p className="text-zinc-500 font-mono">Loading schedule…</p>
        ) : (
          <div data-testid="schedule-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
            {slots.map((slot, i) => {
              const taken = slot.status === "booked";
              return (
                <div
                  key={slot.date}
                  data-testid={`schedule-slot-${i}`}
                  className={`relative p-6 transition-colors ${
                    taken ? "bg-[#0F0F0F]" : "bg-[#0A0A0A] hover:bg-[#121212]"
                  }`}
                >
                  <p className="text-[10px] tracking-[0.25em] uppercase font-mono text-[#1E6FE0]">
                    Week {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-2 font-display text-2xl uppercase leading-tight text-white">
                    {formatDate(slot.date)}
                  </h3>
                  <p className="mt-2 text-xs text-zinc-500 flex items-center gap-1">
                    <Clock className="w-3 h-3 text-[#E10600]" />
                    {slot.time}
                  </p>

                  <div className="mt-6">
                    {taken ? (
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono">
                          <Lock className="w-3 h-3" />
                          Booked
                          {slot.booked_guest && (
                            <span className="text-zinc-400 normal-case tracking-normal">
                              · {slot.booked_guest}
                            </span>
                          )}
                        </span>
                      </div>
                    ) : (
                      <button
                        data-testid={`book-slot-${i}`}
                        onClick={() => startBooking(slot.date)}
                        className="btn-primary w-full justify-center"
                      >
                        Claim This Date
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Booking modal */}
      {openSlot && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md p-4 flex items-center justify-center"
          onClick={() => setOpenSlot(null)}
          data-testid="book-modal"
        >
          <div
            className="bg-[#0A0A0A] border border-[#E10600] max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpenSlot(null)}
              data-testid="book-modal-close"
              className="absolute top-4 right-4 z-10 w-9 h-9 border border-[#262626] hover:border-[#E10600] flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-6 md:p-10">
              {submitted ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="w-12 h-12 text-[#E10600] mx-auto mb-4" />
                  <h2 className="font-display text-3xl uppercase">Booking Received</h2>
                  <p className="mt-3 text-zinc-400">
                    Your slot is held as pending. We'll email you to confirm details
                    within 48 hours.
                  </p>
                  <button
                    onClick={() => setOpenSlot(null)}
                    className="btn-secondary mt-8"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#1E6FE0] mb-2">
                    Claiming
                  </p>
                  <h2 className="font-display text-3xl md:text-4xl uppercase leading-tight">
                    {formatDate(openSlot)}
                  </h2>
                  <p className="mt-2 text-sm text-zinc-400">
                    7:00 PM – 9:00 PM EST · NYC or Boston studio (remote OK)
                  </p>

                  <form
                    onSubmit={submit}
                    className="mt-8 space-y-5"
                    data-testid="book-form"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <Field label="Full Name *">
                        <input data-testid="book-input-name" required value={form.full_name} onChange={update("full_name")} className="input-edge" placeholder="Your name" />
                      </Field>
                      <Field label="Email *">
                        <input data-testid="book-input-email" required type="email" value={form.email} onChange={update("email")} className="input-edge" placeholder="you@example.com" />
                      </Field>
                      <Field label="Phone">
                        <input data-testid="book-input-phone" value={form.phone} onChange={update("phone")} className="input-edge" placeholder="(555) 555-5555" />
                      </Field>
                      <Field label="Social Handle">
                        <input data-testid="book-input-social" value={form.social_handle} onChange={update("social_handle")} className="input-edge" placeholder="@yourhandle" />
                      </Field>
                    </div>
                    <Field label="Topic / Angle *">
                      <input data-testid="book-input-topic" required value={form.topic} onChange={update("topic")} className="input-edge" placeholder="What you want to talk about" />
                    </Field>
                    <Field label="Short Bio">
                      <textarea data-testid="book-input-bio" rows={3} value={form.bio} onChange={update("bio")} className="input-edge resize-none" placeholder="A line or two about you." />
                    </Field>
                    <div className="pt-2 flex flex-wrap items-center gap-4 justify-between">
                      <p className="text-xs text-zinc-500 font-mono">
                        Status starts as PENDING. We confirm within 48 hours.
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="book-submit"
                        className="btn-primary disabled:opacity-50"
                      >
                        {submitting ? "Booking…" : "Lock In My Slot"}
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono block mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
